import {i18n} from '@online-offer/i18n';
import {
  Box,
  Device,
  Grid,
  GridItem,
  Hide,
  Image,
  Integer,
  Space,
  ThemeIconKey,
  useDevice,
} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {Fragment} from 'react';

import {always, any, values} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {DataColumn} from '../../components/DataColumn/DataColumn';
import {Section} from '../../components/Section/Section';
import {Separator} from '../../components/Separator/Separator';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {ElectricalItem} from './components/ElectricalItem';
import {getElectricalParameters} from './utils/getElectricalParameters';

function GridElectricalItem({
  icon,
  categoryTitle,
  value,
  labelTooltip,
}: {
  value: string | Nullish;
  categoryTitle: string;
  icon: ThemeIconKey;
  labelTooltip?: string | Nullish;
}) {
  if (isNilOrEmpty(value)) {
    return null;
  }
  return (
    <GridItem>
      <ElectricalItem
        value={value!}
        categoryTitle={categoryTitle}
        icon={icon}
        labelTooltip={labelTooltip}
      />
    </GridItem>
  );
}

const getElectricalParamBasedOnKey = (
  key: string
): {icon: ThemeIconKey; title: string; labelTooltip?: string | Nullish} => {
  return match<string, {icon: ThemeIconKey; title: string; labelTooltip?: string | Nullish}>(key)
    .with(
      'batteryCapacity',
      always({
        icon: 'electricalParams/battery_capacity',
        title: i18n.t('electricalParameters.item.batteryCapacity'),
      })
    )
    .with(
      'batteryWarranty',
      always({
        icon: 'electricalParams/warranty',
        title: i18n.t('electricalParameters.item.batteryWarranty'),
      })
    )
    .with(
      'electricRange',
      always({
        icon: 'specification/mileage',
        title: i18n.t('electricalParameters.item.electricRange'),
        labelTooltip: i18n.t('electricalParameters.item.electricRangeTooltip'),
      })
    )
    .with(
      'chargingPowerAc',
      always({
        icon: 'electricalParams/charging',
        title: i18n.t('electricalParameters.item.chargingPowerAc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingPowerAcTooltip'),
      })
    )
    .with(
      'chargingPowerDc',
      always({
        icon: 'electricalParams/charging',
        title: i18n.t('electricalParameters.item.chargingPowerDc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingPowerDcTooltip'),
      })
    )
    .with(
      'electricEnginePower',
      always({
        icon: 'electricalParams/voltage',
        title: i18n.t('electricalParameters.item.electricEnginePower'),
        labelTooltip: i18n.t('electricalParameters.item.electricEnginePowerTooltip'),
      })
    )
    .with(
      'chargingTimeAc',
      always({
        icon: 'electricalParams/connector_ac',
        title: i18n.t('electricalParameters.item.chargingTimeAc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingTimeAcTooltip'),
      })
    )
    .with(
      'chargingTimeDc',
      always({
        icon: 'electricalParams/connector_dc',
        title: i18n.t('electricalParameters.item.chargingTimeDc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingTimeDcTooltip'),
      })
    )
    .with(
      'electricEngineConsumption',
      always({
        icon: 'electricalParams/battery_consumption',
        title: i18n.t('electricalParameters.item.electricEngineConsumption'),
        labelTooltip: i18n.t('electricalParameters.item.electricEngineConsumptionTooltip'),
      })
    )
    .with(
      'batteryVoltage',
      always({
        icon: 'electricalParams/voltage',
        title: i18n.t('electricalParameters.item.batteryVoltage'),
        labelTooltip: i18n.t('electricalParameters.item.batteryVoltageTooltip'),
      })
    )
    .otherwise(() => ({
      icon: 'electricalParams/voltage',
      title: key,
    }));
};

export function ElectricalParameters() {
  const {vehicle, alphaCatalogue} = useGetOnlineOfferData();
  const device = useDevice();
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();
  const data = getElectricalParameters(vehicle, alphaCatalogue, formatDateTime, formatNumber);

  if (!vehicle || !data || !any(isNotNilOrEmpty, values(data))) {
    return null;
  }

  const {electricalData, additionalElectricalData, hybridType} = data;

  if (
    !any(isNotNilOrEmpty, values(electricalData)) ||
    !any(isNotNilOrEmpty, values(additionalElectricalData))
  ) {
    return null;
  }

  const columns = match(device)
    .with('mobile', always(1))
    .with('tablet', always(3))
    .with('notebook', always(3))
    .otherwise(always(4));

  const additionalDataColumns = match(device)
    .with('mobile', always(1))
    .with('tablet', always(2))
    .with('notebook', always(2))
    .otherwise(always(2));

  const separatorWidth = match<Device, '50%' | '100%'>(device)
    .with('mobile', always('100%'))
    .otherwise(always('50%'));

  const carImageDimensions = match<Device, {width: Integer; bottom: Integer; right: Integer}>(
    device
  )
    .with(
      'tablet',
      always({
        width: 59,
        bottom: 19,
        right: 0,
      })
    )
    .with(
      'notebook',
      always({
        width: 59,
        bottom: 23,
        right: 0,
      })
    )
    .otherwise(
      always({
        width: 78,
        bottom: 3,
        right: 0,
      })
    );

  return (
    <Section
      id="ELECTRICAL_PARAMETERS"
      isWithGradient={device !== 'mobile'}
      cardContainerFlagProps={
        hybridType
          ? {
              color: 'severity.informationalBase',
              size: 'small',
              textColor: 'primary',
              text: hybridType,
            }
          : undefined
      }
      isWithBreakLine
    >
      <Hide when={device === 'mobile'}>
        <Box
          position="absolute"
          bottom={carImageDimensions.bottom}
          right={carImageDimensions.right}
          overflow="hidden"
        >
          <Image
            isLazy
            maxWidth={carImageDimensions.width}
            fit="cover"
            width="100%"
            height="100%"
            src="../../assets/images/electrical-parameters-car.svg"
          />
        </Box>
      </Hide>
      <Grid columns={columns} verticalSpacing={[8, 8, 8, 10]}>
        {Object.entries(electricalData)
          .filter(([_, value]) => value !== null)
          .map(([key, value], index) => {
            const electricalItem = getElectricalParamBasedOnKey(key);
            return (
              <Fragment key={key}>
                <GridElectricalItem
                  value={value}
                  categoryTitle={electricalItem.title}
                  icon={electricalItem.icon}
                  labelTooltip={electricalItem.labelTooltip}
                />
                {columns === 4 && (index + 1) % 3 === 0 && <GridItem />}
              </Fragment>
            );
          })}
      </Grid>
      <Space vertical={8} />
      <Box width={separatorWidth}>
        <Separator />
      </Box>
      <Space vertical={8} />
      <Grid columns={additionalDataColumns}>
        <GridItem>
          <DataColumn
            data={[
              {
                label: i18n.t('electricalParameters.dataColumn.rangeExtender'),
                value:
                  additionalElectricalData.hasRangeExtender === true
                    ? i18n.t('general.labels.yes')
                    : null,
              },
              {
                label: i18n.t('electricalParameters.dataColumn.batteryType'),
                value: additionalElectricalData.batteryType,
              },
              {
                label: i18n.t('electricalParameters.dataColumn.chargingConnectorType'),
                value: additionalElectricalData.chargingConnectorType,
              },
              {
                label: i18n.t('electricalParameters.dataColumn.numberOfBatteries'),
                value: additionalElectricalData.numberOfBatteries,
              },
              {
                label: i18n.t('electricalParameters.dataColumn.batteryOwnershipType'),
                value: additionalElectricalData.batteryOwnershipType,
                labelTooltip: i18n.t('electricalParameters.dataColumn.batteryOwnershipTypeTooltip'),
              },
            ]}
          />
        </GridItem>
      </Grid>
    </Section>
  );
}
